import React, { useEffect, useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Drawer,
  IconButton,
  TextField,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects";
import FlagIcon from "@material-ui/icons/Flag";
import NoteIcon from "@material-ui/icons/Note";
import EventIcon from "@material-ui/icons/Event";
import MonetizationOnIcon from "@material-ui/icons/MonetizationOn";
import BookmarkIcon from "@material-ui/icons/Bookmark";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import { useVoice } from "../../context/Voice/VoiceContext";
import * as apiVoice from "../../services/apiVoice";
import * as apiTasks from "../../services/apiTasks";
import api from "../../services/api";
import QuickChargeButton from "../QuickChargeButton";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";

/**
 * Post-call Outcome Side Panel — the one screen that leads the agent from "call
 * ended" to "next action", without leaving the Conversation Workspace. Opens
 * automatically when a call ends (skippable). Every action writes to an existing
 * store so it feeds Voice Intelligence: outcome → Agent Performance/Dashboard,
 * promise-to-pay/charge → Collection Center, note → Customer Memory/Timeline,
 * task → Tasks, deal-won → closes the ticket + outcome=sale. Built on the exact
 * ContactDrawer DNA (drawer width, header gradient stripe, section cards).
 */
const drawerWidth = 360;

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const surface = dark ? "#131614" : "#ffffff";
  const surfaceSubtle = dark ? "rgba(255,255,255,0.05)" : "#f6faf7";
  const hair = dark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.06)";
  const textPrimary = dark ? "#e6f7ec" : "#0B0D0C";
  const textMuted = dark ? "rgba(230,247,236,0.55)" : "#5f6f6a";
  const brand = dark ? "#7df0a8" : "#128c7e";
  const iconPillBg = dark
    ? "linear-gradient(135deg, rgba(46,227,122,0.18), rgba(18,140,126,0.16))"
    : "linear-gradient(135deg, rgba(37,211,102,0.14), rgba(18,140,126,0.10))";
  return {
    paper: {
      width: drawerWidth,
      background: dark ? "#0B0D0C" : "#fafafa",
      [theme.breakpoints.down("xs")]: { width: "100%" },
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "14px 16px",
      borderBottom: `1px solid ${hair}`,
      position: "relative",
      background: surface,
      "&::before": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        background: "linear-gradient(90deg, #25d366 0%, #128c7e 60%, transparent 100%)",
        opacity: dark ? 0.55 : 0.7,
      },
    },
    headTitle: { fontSize: "1rem", fontWeight: 700, color: textPrimary },
    headSub: { fontSize: 12, color: textMuted, marginTop: 2 },
    body: { padding: 12, display: "flex", flexDirection: "column", gap: 12, overflowY: "auto" },
    section: {
      background: surface,
      border: `1px solid ${hair}`,
      borderRadius: 14,
      padding: 14,
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },
    sectionHeader: { display: "flex", alignItems: "center", gap: 10 },
    iconPill: {
      width: 32,
      height: 32,
      borderRadius: 10,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: iconPillBg,
      color: brand,
      flexShrink: 0,
      "& svg": { fontSize: 18 },
    },
    sectionTitle: { fontSize: "0.88rem", fontWeight: 700, color: textPrimary, letterSpacing: 0.2 },
    summaryBox: {
      fontSize: 13,
      lineHeight: 1.55,
      background: surfaceSubtle,
      border: `1px solid ${hair}`,
      borderRadius: 10,
      padding: "8px 10px",
      color: textPrimary,
      whiteSpace: "pre-wrap",
    },
    chips: { display: "flex", gap: 6, flexWrap: "wrap" },
    chip: { cursor: "pointer" },
    row: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
    grow: { flex: 1, minWidth: 90 },
    saveBtn: { alignSelf: "flex-start" },
    muted: { fontSize: 12, color: textMuted },
    footer: {
      padding: 12,
      borderTop: `1px solid ${hair}`,
      display: "flex",
      gap: 8,
      background: surface,
    },
  };
});

const OUTCOMES = [
  { key: "sale", label: "מכירה" },
  { key: "follow_up", label: "מעקב" },
  { key: "support", label: "תמיכה" },
  { key: "payment", label: "תשלום" },
  { key: "complaint", label: "תלונה" },
  { key: "no_answer", label: "אין מענה" },
];
const MEMORY_TYPES = [
  { key: "fact", label: "עובדה" },
  { key: "preference", label: "העדפה" },
  { key: "risk", label: "סיכון" },
  { key: "commitment", label: "התחייבות" },
];

const Section = ({ classes, icon, title, children }) => (
  <div className={classes.section}>
    <div className={classes.sectionHeader}>
      <span className={classes.iconPill}>{icon}</span>
      <span className={classes.sectionTitle}>{title}</span>
    </div>
    {children}
  </div>
);

const CallOutcomePanel = ({ ticket }) => {
  const classes = useStyles();
  const { status, outcomeCall, closeOutcome } = useVoice();
  const call = outcomeCall;
  const contactId = ticket?.contactId || ticket?.contact?.id;

  const [outcome, setOutcome] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [notes, setNotes] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [taskDate, setTaskDate] = useState("");
  const [promiseAmount, setPromiseAmount] = useState("");
  const [promiseDate, setPromiseDate] = useState("");
  const [memoryValue, setMemoryValue] = useState("");
  const [memoryType, setMemoryType] = useState("fact");
  const [saving, setSaving] = useState(null);

  // Reset per call.
  useEffect(() => {
    setOutcome(call?.outcome || null);
    setAnalysis(call?.analysis || null);
    setNotes(call?.notes || "");
    setTaskTitle("");
    setTaskDate("");
    setPromiseAmount("");
    setPromiseDate("");
    setMemoryValue("");
    setMemoryType("fact");
  }, [call]);

  const guard = useCallback(
    async (key, fn, okMsg) => {
      setSaving(key);
      try {
        await fn();
        if (okMsg) toast.success(okMsg);
        return true;
      } catch (err) {
        toastError(err);
        return false;
      } finally {
        setSaving(null);
      }
    },
    []
  );

  if (!status || !status.enabled) return null;

  const open = Boolean(call);
  const sec = (s) => `${Math.floor((s || 0) / 60)}:${String((s || 0) % 60).padStart(2, "0")}`;

  const tagOutcome = (key) => {
    setOutcome(key);
    guard("outcome", () => apiVoice.setCallOutcome(call.id, key));
  };

  const runAnalyze = () =>
    guard(
      "ai",
      async () => {
        setAnalyzing(true);
        try {
          // ensure transcript then analyze (on-demand)
          await apiVoice.processCall(call.id, true).catch(() => {});
          for (let i = 0; i < 14; i++) {
            // eslint-disable-next-line no-await-in-loop
            const a = await apiVoice.analyzeCall(call.id).catch(() => null);
            if (a && a.summary) {
              setAnalysis(a);
              return;
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise((r) => setTimeout(r, 3000));
          }
        } finally {
          setAnalyzing(false);
        }
      },
      null
    );

  const saveNotes = () =>
    guard("notes", () => apiVoice.setCallNotes(call.id, notes), "ההערה נשמרה");

  const createTaskAction = () => {
    if (!taskTitle.trim()) return;
    guard(
      "task",
      () =>
        apiTasks.createTask({
          title: taskTitle.trim(),
          ticketId: ticket?.id,
          dueDate: taskDate || undefined,
          reminderDate: taskDate || undefined,
          priority: "medium",
          status: "pending",
        }),
      "המשימה נוצרה"
    ).then((ok) => {
      if (!ok) return;
      setTaskTitle("");
      setTaskDate("");
    });
  };

  const createPromise = () => {
    const amt = parseFloat(promiseAmount);
    if (!Number.isFinite(amt) || amt <= 0) {
      toast.error("סכום לא תקין");
      return;
    }
    guard(
      "promise",
      () =>
        apiVoice.createCollection({
          contactId,
          ticketId: ticket?.id,
          callSessionId: call.id,
          amountDue: amt,
          reason: promiseDate ? `התחייבות לתשלום עד ${promiseDate}` : "התחייבות לתשלום",
          notes: promiseDate ? `תאריך יעד: ${promiseDate}` : undefined,
        }),
      "ההתחייבות נרשמה במרכז הגבייה"
    ).then((ok) => {
      if (!ok) return;
      setPromiseAmount("");
      setPromiseDate("");
    });
  };

  const saveMemory = () => {
    if (!memoryValue.trim()) return;
    guard(
      "memory",
      () =>
        apiVoice.createMemory(contactId, {
          type: memoryType,
          value: memoryValue.trim(),
          sourceType: "call",
          sourceRef: `callSession:${call.id}`,
          status: "confirmed",
        }),
      "נשמר לזיכרון הלקוח"
    ).then((ok) => { if (ok) setMemoryValue(""); });
  };

  const dealWon = () =>
    guard(
      "deal",
      async () => {
        await apiVoice.setCallOutcome(call.id, "sale");
        setOutcome("sale");
        if (ticket?.id) await api.put(`/tickets/${ticket.id}`, { status: "closed" });
      },
      "העסקה נסגרה 🎉"
    ).then((ok) => { if (ok) closeOutcome(); });

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={closeOutcome}
      classes={{ paper: classes.paper }}
    >
      <div className={classes.header}>
        <div>
          <div className={classes.headTitle}>סיכום שיחה ופעולה</div>
          {call && (
            <div className={classes.headSub}>
              {call.toNumber || "לקוח"}
              {call.durationSec > 0 ? ` · ${sec(call.durationSec)} דק׳` : ""}
            </div>
          )}
        </div>
        <IconButton size="small" onClick={closeOutcome} aria-label="סגור">
          <CloseIcon />
        </IconButton>
      </div>

      {call && (
        <div className={classes.body}>
          {/* 1. AI Summary */}
          <Section classes={classes} icon={<EmojiObjectsIcon />} title="סיכום AI">
            {analysis && analysis.summary ? (
              <div className={classes.summaryBox}>
                {analysis.summary}
                <div className={classes.chips} style={{ marginTop: 6 }}>
                  {analysis.intent && <Chip size="small" variant="outlined" label={analysis.intent} />}
                  {analysis.sentiment && <Chip size="small" variant="outlined" label={analysis.sentiment} />}
                </div>
              </div>
            ) : call.recordingRequested ? (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                onClick={runAnalyze}
                disabled={analyzing}
                startIcon={analyzing ? <CircularProgress size={14} /> : <EmojiObjectsIcon />}
              >
                {analyzing ? "מנתח…" : "נתח שיחה"}
              </Button>
            ) : (
              <span className={classes.muted}>אין הקלטה לניתוח.</span>
            )}
          </Section>

          {/* 2. Outcome */}
          <Section classes={classes} icon={<FlagIcon />} title="תוצאת שיחה">
            <div className={classes.chips}>
              {OUTCOMES.map((o) => (
                <Chip
                  key={o.key}
                  size="small"
                  className={classes.chip}
                  variant={outcome === o.key ? "default" : "outlined"}
                  color={outcome === o.key ? "primary" : "default"}
                  label={o.label}
                  onClick={() => tagOutcome(o.key)}
                />
              ))}
            </div>
          </Section>

          {/* 3. Notes */}
          <Section classes={classes} icon={<NoteIcon />} title="הערה">
            <TextField
              multiline
              minRows={2}
              size="small"
              variant="outlined"
              fullWidth
              placeholder="מה קרה בשיחה?"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
            <Button
              size="small"
              className={classes.saveBtn}
              onClick={saveNotes}
              disabled={saving === "notes"}
              startIcon={saving === "notes" ? <CircularProgress size={14} /> : null}
            >
              שמור הערה
            </Button>
          </Section>

          {/* 4. Follow Up / Task */}
          <Section classes={classes} icon={<EventIcon />} title="מעקב / משימה">
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              placeholder="כותרת המשימה"
              value={taskTitle}
              onChange={(e) => setTaskTitle(e.target.value)}
            />
            <div className={classes.row}>
              <TextField
                size="small"
                variant="outlined"
                type="datetime-local"
                className={classes.grow}
                InputLabelProps={{ shrink: true }}
                value={taskDate}
                onChange={(e) => setTaskDate(e.target.value)}
              />
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={createTaskAction}
                disabled={saving === "task" || !taskTitle.trim()}
              >
                צור משימה
              </Button>
            </div>
          </Section>

          {/* 5. Promise To Pay */}
          <Section classes={classes} icon={<MonetizationOnIcon />} title="התחייבות לתשלום">
            <div className={classes.row}>
              <TextField
                size="small"
                variant="outlined"
                type="number"
                className={classes.grow}
                placeholder="סכום ₪"
                value={promiseAmount}
                onChange={(e) => setPromiseAmount(e.target.value)}
              />
              <TextField
                size="small"
                variant="outlined"
                type="date"
                className={classes.grow}
                InputLabelProps={{ shrink: true }}
                value={promiseDate}
                onChange={(e) => setPromiseDate(e.target.value)}
              />
            </div>
            <Button
              size="small"
              className={classes.saveBtn}
              onClick={createPromise}
              disabled={saving === "promise"}
            >
              רשום התחייבות
            </Button>
          </Section>

          {/* 6. Save to Customer Memory */}
          <Section classes={classes} icon={<BookmarkIcon />} title="זיכרון לקוח">
            <TextField
              size="small"
              variant="outlined"
              fullWidth
              placeholder="מה חשוב לזכור על הלקוח?"
              value={memoryValue}
              onChange={(e) => setMemoryValue(e.target.value)}
            />
            <div className={classes.row}>
              <TextField
                select
                size="small"
                variant="outlined"
                className={classes.grow}
                value={memoryType}
                onChange={(e) => setMemoryType(e.target.value)}
              >
                {MEMORY_TYPES.map((t) => (
                  <MenuItem key={t.key} value={t.key}>
                    {t.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                size="small"
                variant="contained"
                color="primary"
                onClick={saveMemory}
                disabled={saving === "memory" || !memoryValue.trim()}
              >
                שמור
              </Button>
            </div>
          </Section>
        </div>
      )}

      {/* Footer: quick charge + deal won + skip */}
      <div className={classes.footer}>
        {ticket && call && (
          <span title="חיוב מהיר" style={{ display: "inline-flex" }}>
            <QuickChargeButton ticket={ticket} />
          </span>
        )}
        <Button
          variant="contained"
          color="primary"
          startIcon={<CheckCircleIcon />}
          onClick={dealWon}
          disabled={saving === "deal"}
          style={{ flex: 1 }}
        >
          עסקה נסגרה
        </Button>
        <Button onClick={closeOutcome}>דלג</Button>
      </div>
    </Drawer>
  );
};

export default CallOutcomePanel;
